import { css } from '@emotion/react'
import styled from '@emotion/styled'
import * as React from 'react'
import { ChangeEvent, ForwardedRef, forwardRef, useMemo, useState } from 'react'
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { Transitions } from '~/assets/style/tokens'
import { WithClassName } from '~/types/utils'
import { Icon } from './Icon'
import { BaseInputElement, InputFieldset, InputTextWrapper, NativeInput, BaseInputProps } from './Input'
import { SelectableOptionsPopover, SelectOptionProp } from './SelectableOptionsPopover'

type BaseSelectProps = WithClassName &
  Omit<BaseInputProps, 'value' | 'onChange' | 'label' | 'variant'> & {
    label: string
    onChange?: (option: SelectOptionProp) => unknown
    options: SelectOptionProp[]
    value: string | number | undefined
    searchable?: boolean
    searchDisabledValueErrorMessage?: string
    error?: string | React.ReactNode // TODO try to write a type with error property only if it's searchable (without raise an issue)
    showMultiLevelAsCategory?: boolean
    variant?: 'small' | 'regular' | 'inline'
    highlight?: boolean
  }

export const SelectInput = styled(BaseInputElement)<{ isOpen: boolean; search: boolean; highlight?: boolean; selectVariant?: 'small' | 'regular' | 'inline' }>`
  ${NativeInput} {
    cursor: pointer;
    caret-color: ${({ search }) => (search ? Colors.Jet : 'transparent')};

    padding: ${({ selectVariant }) => (selectVariant === 'inline' ? '0' : selectVariant === 'small' ? '4px 0' : '10px 0')};
    ${({ selectVariant }) =>
      selectVariant === 'inline' &&
      css`
        visibility: hidden;
        width: 0;
      `}
  }

  & ${InputFieldset} {
    border: ${({ selectVariant, highlight }) => (selectVariant === 'inline' ? 'none' : highlight ? `1px solid ${Colors.Ming}` : `1px solid ${Colors.Platinum}`)} ;
  }

  & ${Icon} {
    cursor: pointer;
    transition: transform ${Transitions.quick};
    transform: rotate(${({ isOpen }) => (isOpen ? '-180deg' : '0')});
  }

  & ${InputTextWrapper} {
    padding: 0 ${({ selectVariant }) => (selectVariant === 'small' ? '8px' : selectVariant === 'inline' ? '0' : '12px')};
  }
`

const InlineInputText = styled.div`
  font-weight: 500;
  cursor: pointer;
  ${Fonts.colors.King}

  :hover {
    ${Fonts.colors.Navy}
  }
`

const getOptionValue = (options: SelectOptionProp[], value: string | number | undefined) =>
  options.flatMap((option) => (option.value instanceof Array ? option.value : option)).find((option) => option.value === value) || { label: '' }
const filterOptionsBySearch = (options: SelectOptionProp[], searchValue: string): SelectOptionProp[] => {
  const includesSearch = (option: SelectOptionProp) => option.label.toLowerCase().includes(searchValue.toLowerCase())
  return options
    .map((option) => {
      if (option.value instanceof Array) {
        let newValue = filterOptionsBySearch(option.value, searchValue)
        if (!newValue.length) {
          newValue = option.value
        }
        return { ...option, value: newValue }
      }
      return option
    })
    .filter((option) => {
      if (option.value instanceof Array) {
        return includesSearch(option) || option.value.some(includesSearch)
      }
      return includesSearch(option)
    })
}

const useSortedOptions = (options: SelectOptionProp[]) =>
  useMemo((): SelectOptionProp[] => {
    const sortOptions = (options: SelectOptionProp[]) =>
      [...options].sort((o1, o2) => {
        if (o1.disabled && !o2.disabled) {
          return 1
        } else if (!o1.disabled && o2.disabled) {
          return -1
        } else {
          return 0
        }
      })
    return sortOptions(
      [...options].map((option) => {
        if (option.value instanceof Array) {
          return { ...option, value: sortOptions(option.value) }
        } else {
          return option
        }
      })
    )
  }, [options])

const UnstyledSelect = (
  { className, options: unsortedOptions, label, value, onChange, searchable, error, isClearable = false, showMultiLevelAsCategory, highlight, ...props }: BaseSelectProps,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const options = useSortedOptions(unsortedOptions)
  const [open, setOpen] = useState(false)
  // seeded from the selected option, the way the mount-time effect used to
  const [searchValue, setSearchValue] = useState(() => {
    const option = getOptionValue(options, value)
    return option.label || `${value ?? ''}`
  })
  const [isSearching, setIsSearching] = useState(false)
  const filteredOptions = isSearching ? filterOptionsBySearch(options, searchValue.trim()) : options
  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    if (!searchable) {
      return
    }

    const value = event.target.value
    setSearchValue(value)
    setIsSearching(true)
    setOpen(true)
  }

  // When the focus is lost while searching we want to select the dropdown option matching with the search if it exists
  const onBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (!isSearching) {
      return
    }

    const getOptionMatchingWithSearch = (options: SelectOptionProp[]): SelectOptionProp | undefined => {
      return options.find((o) => (o.value instanceof Array ? getOptionMatchingWithSearch(o.value) : o.label.toLowerCase() === searchValue.toLowerCase()))
    }

    let matchedOption = getOptionMatchingWithSearch(filteredOptions)
    if (onChange) {
      if (matchedOption) {
        // if the select option was a group, we need to pick the real sub-option that matched
        matchedOption = matchedOption.value instanceof Array ? getOptionMatchingWithSearch(matchedOption.value)! : matchedOption
        setSearchValue(matchedOption.label) // force the input to have the good label to avoid an inconsistent display
        onChange(matchedOption)
      } else {
        onChange({ label: searchValue, value: searchValue, trackingLabel: 'common-selection-unknown-apply' })
      }
    }
    if (props.onBlur) {
      props.onBlur(e)
    }
  }

  const [previousOptions, setPreviousOptions] = useState(options)
  const [previousValue, setPreviousValue] = useState(value)
  const [previousOpen, setPreviousOpen] = useState(open)

  // the input text mirrors the selected option: realign on change, during render rather than in an effect
  if (previousOptions !== options || previousValue !== value) {
    setPreviousOptions(options)
    setPreviousValue(value)
    const option = getOptionValue(options, value)
    setSearchValue(option.label || `${value ?? ''}`)
  }

  // closing the dropdown ends the search
  if (previousOpen !== open) {
    setPreviousOpen(open)
    if (!open) {
      setIsSearching(false)
    }
  }

  const selectInput = (
    <SelectInput
      {...props}
      highlight={highlight}
      search={isSearching}
      isOpen={open}
      className={className}
      iconRight={'chevron_down'}
      onKeyDown={(event) => {
        if (event.code === 'Space' && !isSearching) {
          setOpen(!open)
        }
      }}
      label={label}
      value={searchValue}
      onChange={onSearch}
      ref={ref}
      error={error}
      onBlur={onBlur}
      isClearable={isClearable}
      onClear={() => {
        isClearable && onChange && onChange({ label: '', value: '', trackingLabel: 'common-selection-restore' })
      }}
      variant={props.variant === 'inline' ? 'regular' : props.variant}
      selectVariant={props.variant}
      internalInputRenderer={
        props.variant === 'inline'
          ? (inputComponent) => (
              <InlineInputText>
                {searchValue}
                {inputComponent}
              </InlineInputText>
            )
          : undefined
      }
    />
  )

  return props.disabled ? (
    selectInput
  ) : (
    <SelectableOptionsPopover
      trigger={selectInput}
      open={open}
      setOpen={setOpen}
      onChange={onChange}
      options={filteredOptions}
      showMultiLevelAsCategory={showMultiLevelAsCategory}
      maxWidthPopoverSameAsTrigger={true}
    />
  )
}

const BaseSelect = styled(forwardRef<HTMLInputElement, BaseSelectProps>(UnstyledSelect))``

type SelectProps<T extends FieldValues> = Omit<UseControllerProps<T>, 'defaultValue'> & Omit<BaseSelectProps, 'value' | 'ref'> & { error?: string }
const Select = <T extends FieldValues>({ control, onChange, onBlur, name, shouldUnregister, rules, searchDisabledValueErrorMessage, ...props }: SelectProps<T>) => {
  const newRules = { ...rules }
  if (props.searchable) {
    const searchValidateRules: { [key in string]: (v: SelectOptionProp) => string | true } = {
      searchExactMatch: (v: SelectOptionProp) => {
        return (
          props.options
            .flatMap((option) => (option.value instanceof Array ? option.value : option))
            .map((o) => o.value)
            .includes(v) || 'Unexpected value.'
        )
      },
      searchDisabled: (v: SelectOptionProp) => {
        return (
          props.options
            .flatMap((option) => (option.value instanceof Array ? option.value : option))
            .filter((o) => !o.disabled)
            .map((o) => o.value)
            .includes(v) ||
          (searchDisabledValueErrorMessage ?? 'Choose another value.')
        )
      },
    }
    if (typeof newRules.validate === 'function') {
      newRules.validate = { value: newRules.validate, ...searchValidateRules }
    } else if (typeof newRules.validate === 'object') {
      newRules.validate = { ...newRules.validate, ...searchValidateRules }
    } else {
      newRules.validate = searchValidateRules
    }
  }
  return (
    <Controller
      control={control}
      name={name}
      shouldUnregister={shouldUnregister}
      rules={newRules}
      render={({ field }) => (
        <BaseSelect
          {...props}
          ref={field.ref}
          value={field.value as string}
          onBlur={(e) => {
            field.onBlur()
            onBlur && onBlur(e)
          }}
          onChange={(option) => {
            field.onChange(option.value as string)
            onChange && onChange(option)
          }}
        />
      )}
    />
  )
}

export { Select, BaseSelect as UnstyledSelect }
